/**
 * clarityOptout — the SPA half of the Clarity opt-out.
 *
 *   https://mentionfox.com/?clarity_optout=1   Analytics off for this browser
 *   https://mentionfox.com/?clarity_optout=0   Analytics back on
 *
 * A long-lived localStorage flag, so it survives IP rotation and works on a phone
 * with no Clarity admin setting. The static pages carry the same logic inline
 * (scripts/clarity-snippet.cjs) — keep the key, the parameter and the wording in
 * step with that file.
 */
const KEY = "mf_clarity_optout";
const CLARITY_ID = "y9rayhq162";

/**
 * v0.3 (T883, 2026-10-19): never on a sign-in address. Clarity recorded a Google sign-in return
 * (/auth#access_token=...). True for /auth, /auth/*, an OAuth callback, or a query / fragment that
 * carries a token. Same rule as AUTH_GUARD in scripts/clarity-snippet.cjs and authAddress in
 * public/google-tag.js (which holds the loader on such an address and runs it on a clean one).
 */
const AUTH_PATH_RE = /^\/auth(\/|$)|\/oauth(\/|$)|callback(\/|$)/i;
const AUTH_PARAM_RE = /[?#&](access_token|refresh_token|provider_token|provider_refresh_token|id_token|code|token_hash|api_key)=/i;
export function isAuthAddress(loc: { pathname: string; search: string; hash: string } = window.location): boolean {
  return AUTH_PATH_RE.test(loc.pathname) || AUTH_PARAM_RE.test(loc.search + loc.hash);
}

function confirm(message: string) {
  const show = () => {
    const d = document.createElement("div");
    d.setAttribute("role", "status");
    d.setAttribute("data-clarity-optout", "");
    d.style.cssText =
      "position:fixed;left:50%;top:16px;transform:translateX(-50%);z-index:2147483647;background:#111733;color:#F0F2F5;padding:10px 16px;border-radius:8px;font:14px/1.4 system-ui,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.3)";
    d.textContent = message;
    document.body.appendChild(d);
    setTimeout(() => d.remove(), 8000);
  };
  if (document.body) show();
  else document.addEventListener("DOMContentLoaded", show);
}

/** Applies ?clarity_optout=1|0 and reports whether this browser has opted out. */
export function clarityOptedOut(): boolean {
  try {
    const q = new URLSearchParams(window.location.search).get("clarity_optout");
    if (q === "1") { localStorage.setItem(KEY, "1"); confirm("Analytics are now off for this browser"); }
    else if (q === "0") { localStorage.removeItem(KEY); confirm("Analytics are back on for this browser"); }
    return localStorage.getItem(KEY) === "1";
  } catch {
    // Storage blocked (private mode, disabled): there is no flag to honour.
    return false;
  }
}

/** Initialise Clarity unless this browser has opted out, only through the consent gate (v0.2, T285). Safe to call twice. */
export function initClarity(): void {
  if (clarityOptedOut()) {
    // Said out loud so an opt-out can be confirmed from devtools.
    console.info("Clarity: not collecting — analytics opted out in this browser");
    return;
  }
  const w = window as any;
  const load = () => {
    // Checked when the gate RUNS this, not when it is queued: on a sign-in address it loads nothing.
    if (isAuthAddress()) return;
    if (w.clarity && document.querySelector(`script[src*="clarity.ms/tag/${CLARITY_ID}"]`)) return;
    w.clarity = w.clarity || function (...args: unknown[]) { (w.clarity.q = w.clarity.q || []).push(args); };
    const t = document.createElement("script");
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + CLARITY_ID;
    const y = document.getElementsByTagName("script")[0];
    y.parentNode!.insertBefore(t, y);
  };
  // T285 (cc_tasks row 245): Clarity is optional. It loads only through the one consent gate in
  // public/google-tag.js (window.mfConsent): after "Accept All", never before a choice, after
  // "Essential Only", with Global Privacy Control / Do Not Track, or inside a frame.
  // No gate on the page (vite dev, a page without google-tag.js) = Clarity does not load.
  if (w.mfConsent && typeof w.mfConsent.gate === "function") w.mfConsent.gate(load);
}
